import * as React from "react";
import {
  Alert,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Checkbox,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import {
  addCompanyAlias,
  checkEmailDomain,
  fetchCompletedScoreTypesForEmployee,
  fetchEmployeeRoleById,
  findOrCreateCompany,
  getCurrentSession,
  linkEmployee as linkEmployeeQuery,
  requestInvite as requestInviteQuery,
  searchCompaniesFuzzy,
  selfRegister,
  sendPasswordResetEmail,
  signInWithPassword,
  signUpWithPassword,
} from "../../api/queries";
import { useI18n } from "../../i18n/I18nProvider";
import { saveCachedRole } from "../utils/Utils";
import type { DepartmentOption, LinkEmployeeResponse, RequestInviteResponse, SelfRegisterResponse, Step } from "../../types/entry";
import type { CompanySuggestion } from "../../types/company";

type CompanyDecision = "idle" | "confirmed-existing" | "confirmed-new";


const DEPARTMENT_OPTIONS: DepartmentOption[] = [
  { value: "Management", en: "Management", de: "Management" },
  { value: "Operations", en: "Operations", de: "Betrieb" },
  { value: "Human Resources", en: "Human Resources", de: "Personalwesen" },
  { value: "Finance", en: "Finance", de: "Finanzen" },
  { value: "IT", en: "IT", de: "IT" },
  { value: "Sales", en: "Sales", de: "Vertrieb" },
  { value: "Marketing", en: "Marketing", de: "Marketing" },
  { value: "Customer Support", en: "Customer Support", de: "Kundensupport" },
  { value: "Legal", en: "Legal", de: "Recht" },
  { value: "Procurement", en: "Procurement", de: "Einkauf" },
  { value: "Production", en: "Production", de: "Produktion" },
  { value: "Logistics", en: "Logistics", de: "Logistik" },
  { value: "Research and Development", en: "Research and Development", de: "Forschung und Entwicklung" },
  { value: "Administration", en: "Administration", de: "Verwaltung" },
  { value: "Other", en: "Other", de: "Sonstiges" },
];

export default function Entry(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const i18n: any = useI18n();
  const { t } = i18n;
  const lang: "en" | "de" = i18n.lang === "de" ? "de" : "en";
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);

  const [step, setStep] = React.useState<Step>("self-register");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  const [companyText, setCompanyText] = React.useState("");
  const [companyId, setCompanyId] = React.useState("");
  const [companyDecision, setCompanyDecision] = React.useState<CompanyDecision>("idle");
  const [pendingCompanyAlias, setPendingCompanyAlias] = React.useState<string | null>(null);
  const [companySuggestions, setCompanySuggestions] = React.useState<CompanySuggestion[]>([]);
  const [companySuggestionsLoading, setCompanySuggestionsLoading] = React.useState(false);

  const [newCompanyDialogOpen, setNewCompanyDialogOpen] = React.useState(false);
  const [newCompanyName, setNewCompanyName] = React.useState("");
  const [newCompanyAlias, setNewCompanyAlias] = React.useState("");
  const [newCompanySubmitting, setNewCompanySubmitting] = React.useState(false);
  const [newCompanyError, setNewCompanyError] = React.useState<string | null>(null);
  const isMobileViewport = useMediaQuery("(max-width:599.95px)");

  const [department, setDepartment] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [position, setPosition] = React.useState("");
  const [personalAddress, setPersonalAddress] = React.useState("");

  const [registerAnonymous, setRegisterAnonymous] = React.useState(true);
  const [legalAccepted, setLegalAccepted] = React.useState(false);

  const [forgotEmail, setForgotEmail] = React.useState("");
  const [forgotInfo, setForgotInfo] = React.useState<string | null>(null);

  const [error, setError] = React.useState<string | null>(null);
  const emailNorm = email.trim().toLowerCase();
  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#FFFFFF",
    },
    "& .MuiInputLabel-root": {
      fontWeight: 800,
      color: "#374151",
    },
  };

  const openLoginLookup = () => {
    setError(null);
    setPassword("");
    setShowPassword(false);
    setLegalAccepted(false);
    setStep("email");
  };

  // Live "did you mean" suggestions while the user types a company name.
  // Only searches while the field is unconfirmed (idle) -- once the user has
  // confirmed a match or explicitly chosen "new company", typing stops
  // driving suggestions until they hit "Change".
  React.useEffect(() => {
    if (step !== "self-register" || companyDecision !== "idle") return;

    const query = companyText.trim();

    if (!query) {
      setCompanySuggestions([]);
      return;
    }

    let mounted = true;
    setCompanySuggestionsLoading(true);

    const timer = window.setTimeout(async () => {
      const { data, error } = await searchCompaniesFuzzy(query);

      if (!mounted) return;

      setCompanySuggestionsLoading(false);

      if (error) {
        setCompanySuggestions([]);
        return;
      }

      setCompanySuggestions((data ?? []) as CompanySuggestion[]);
    }, 300);

    return () => {
      mounted = false;
      window.clearTimeout(timer);
    };
  }, [step, companyText, companyDecision]);

  const onCompanyTextChange = (value: string) => {
    setCompanyText(value);
    setCompanyDecision("idle");
    setCompanyId("");
    setPendingCompanyAlias(null);
  };

  const onConfirmCompanySuggestion = (suggestion: CompanySuggestion) => {
    const typed = companyText.trim();

    setCompanyId(suggestion.id);
    setCompanyText(suggestion.name);
    setCompanyDecision("confirmed-existing");
    setCompanySuggestions([]);
    setPendingCompanyAlias(typed && typed.toLowerCase() !== suggestion.name.toLowerCase() ? typed : null);
  };

  const onRejectCompanySuggestion = (suggestion: CompanySuggestion) => {
    setCompanySuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
  };

  const onOpenNewCompanyDialog = () => {
    setNewCompanyName(companyText.trim());
    setNewCompanyAlias("");
    setNewCompanyError(null);
    setNewCompanyDialogOpen(true);
  };

  const onCloseNewCompanyDialog = () => {
    if (newCompanySubmitting) return;
    setNewCompanyDialogOpen(false);
  };

  const onSubmitNewCompanyDialog = async () => {
    const name = newCompanyName.trim();
    const alias = newCompanyAlias.trim();

    if (!name) {
      setNewCompanyError(t("entry.err.companyNameRequired"));
      return;
    }

    setNewCompanySubmitting(true);
    setNewCompanyError(null);

    const { data: resolved, error: resolveError } = await findOrCreateCompany(name);

    if (resolveError || !resolved) {
      setNewCompanySubmitting(false);
      setNewCompanyError(resolveError?.message ?? t("entry.err.selfRegisterFailed"));
      return;
    }

    if (alias && alias.toLowerCase() !== resolved.name.toLowerCase()) {
      // Best-effort: a failed alias write shouldn't block registration.
      await addCompanyAlias(resolved.id, alias);
    }

    setCompanyId(resolved.id);
    setCompanyText(resolved.name);
    setCompanyDecision(resolved.created ? "confirmed-new" : "confirmed-existing");
    setCompanySuggestions([]);
    setPendingCompanyAlias(null);
    setNewCompanySubmitting(false);
    setNewCompanyDialogOpen(false);
  };

  const onChangeCompany = () => {
    setCompanyDecision("idle");
    setCompanyId("");
    setPendingCompanyAlias(null);
  };

  const needsReadinessCheck = async (employeeId: string): Promise<boolean> => {
    const { data, error } = await fetchCompletedScoreTypesForEmployee(employeeId);

    if (error) {
      throw new Error(error.message || "Failed to load the scores.");
    }

    const scoreTypes = new Set((data ?? []).map((row: any) => row.score_type));

    const hasAllScores =
      scoreTypes.has("PBG") &&
      scoreTypes.has("Ext_PRS") &&
      scoreTypes.has("E_Score");

    return !hasAllScores;
  };

  const redirectAfterAuth = async (employeeId: string) => {
    const { data: employee, error: employeeError } = await fetchEmployeeRoleById(employeeId);

    if (employeeError) {
      throw new Error(employeeError.message || "Failed to load the account role.");
    }

    const role = String(employee?.company_role ?? "employee").toLowerCase();
    saveCachedRole(role);

    if (role === "admin") {
      handlePageChange("Manager");
      return;
    }

    const needs = await needsReadinessCheck(employeeId);
    handlePageChange(needs ? "ReadinessCheck" : "KontrolScore");
  };

  const requestInvite = async (): Promise<RequestInviteResponse> => {
    const { data, error } = await requestInviteQuery(emailNorm);

    if (error) throw new Error(error.message || "Failed to call request-invite.");
    return data as RequestInviteResponse;
  };

  const registerSelf = async (authUserId: string, resolvedCompanyId: string): Promise<SelfRegisterResponse> => {
    const { data, error } = await selfRegister({
      email: emailNorm,
      authUserId,
      companyId: resolvedCompanyId,
      department: department.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      position: position.trim(),
      personalAddress: personalAddress.trim(),
      anonymous: registerAnonymous,
    });

    if (error) throw new Error(error.message || "Failed to call self-register.");
    return data as SelfRegisterResponse;
  };

  const linkEmployee = async (accessToken?: string): Promise<LinkEmployeeResponse> => {
    let token = accessToken;

    if (!token) {
      const { data: sessionData, error: sessionErr } = await getCurrentSession();
      if (sessionErr) throw new Error(sessionErr.message);
      token = sessionData.session?.access_token;
    }

    if (!token) throw new Error("No session token found (login required).");

    const { data, error } = await linkEmployeeQuery(emailNorm, token);

    if (error) throw new Error(error.message || "Failed to link employee.");
    return data as LinkEmployeeResponse;
  };

  const linkAfterAuth = async (accessToken?: string) => {
    const linked = await linkEmployee(accessToken);
    if (!linked.ok) throw new Error(linked.reason || "Failed to link employee.");
    await redirectAfterAuth(linked.employee_id);
  };

  const submitEmail = async () => {
    setError(null);

    if (!emailNorm || !emailNorm.includes("@")) {
      setError(t("entry.err.invalidEmail"));
      return;
    }

    setStep("loading");

    try {
      const res = await requestInvite();

      if (!res.ok) {
        setError(t("entry.err.notFound"));
        setStep("email");
        return;
      }

      setPassword("");
      setShowPassword(false);
      setStep(res.mode === "login" ? "login" : "set-password");
    } catch (e: any) {
      setError(e?.message ?? t("entry.err.edgeInvoke"));
      setStep("email");
    }
  };

  const openForgot = () => {
    setError(null);
    setForgotInfo(null);
    setForgotEmail(emailNorm || "");
    setPassword("");
    setShowPassword(false);
    setStep("forgot");
  };

  const sendResetEmail = async () => {
    setError(null);
    setForgotInfo(null);

    const e = forgotEmail.trim().toLowerCase();

    if (!e || !e.includes("@")) {
      setForgotInfo(t("entry.reset.invalidEmail"));
      return;
    }

    const { data, error } = await sendPasswordResetEmail(e, window.location.origin);

    if (error || !data?.ok) {
      setForgotInfo(t("entry.reset.sent"));
      return;
    }

    setForgotInfo(t("entry.reset.sent"));
  };

  const login = async () => {
    setError(null);

    if (!password) {
      setError(t("entry.err.noPassword"));
      return;
    }

    setStep("loading");

    const { data, error: signInError } = await signInWithPassword(emailNorm, password);

    if (signInError) {
      setError(signInError.message);
      setStep("login");
      return;
    }

    if (!data.user?.id) {
      setError(t("entry.err.edgeInvoke"));
      setStep("login");
      return;
    }

    try {
      await linkAfterAuth();
    } catch (e: any) {
      setError(e?.message ?? t("entry.err.linkFailed"));
      setStep("login");
    }
  };

  const setInitialPassword = async () => {
    setError(null);

    if (!password || password.length < 8) {
      setError(t("entry.err.pwTooShort"));
      return;
    }

    setStep("loading");

    const { data: signUpData, error: signUpError } = await signUpWithPassword(emailNorm, password);

    if (signUpError) {
      const msg = (signUpError.message || "").toLowerCase();
      if (
        msg.includes("already registered") ||
        msg.includes("already exists") ||
        msg.includes("database error saving new user")
      ) {
        setError(null);
        setPassword("");
        setShowPassword(false);
        setStep("login");
        return;
      }

      setError(signUpError.message);
      setStep("set-password");
      return;
    }

    if (!signUpData.user?.id) {
      setError("Failed to create account.");
      setStep("set-password");
      return;
    }

    try {
      setPassword("");
      setShowPassword(false);
      await linkAfterAuth(signUpData.session?.access_token);
    } catch (e: any) {
      setError(e?.message ?? t("entry.err.linkFailed"));
      setStep("set-password");
    }
  };

  const submitSelfRegistration = async () => {
    setError(null);

    if (!emailNorm || !emailNorm.includes("@")) {
      setError(t("entry.err.invalidEmail"));
      return;
    }

    if (!password || password.length < 8) {
      setError(t("entry.err.pwTooShort"));
      return;
    }

    if (!companyText.trim()) {
      setError(t("entry.err.companyRequired"));
      return;
    }

    if (companyDecision === "idle") {
      setError(t("entry.err.companyUnconfirmed"));
      return;
    }

    if (!department.trim()) {
      setError(t("entry.err.departmentRequired"));
      return;
    }

    if (!registerAnonymous && (!firstName.trim() || !lastName.trim())) {
      setError(t("entry.err.nameRequired"));
      return;
    }

    if (!registerAnonymous && !position.trim()) {
      setError(t("entry.err.positionRequired"));
      return;
    }

    if (!registerAnonymous && !personalAddress.trim()) {
      setError(t("entry.err.personalAddressRequired"));
      return;
    }

    if (!legalAccepted) {
      setError(
        L(
          "Please accept the Privacy Policy and Terms & Conditions to continue.",
          "Bitte akzeptiere die Datenschutzerklärung und die AGB, um fortzufahren."
        )
      );
      return;
    }

    setStep("loading");

    try {
      const { data: domainCheck, error: domainCheckError } = await checkEmailDomain(emailNorm);

      // Fail open on a broken check itself (network hiccup, function
      // outage) -- only a definitive "no" blocks registration. The check
      // runs server-side (an edge function) rather than from the browser
      // since this app's CSP only allows connecting to 'self' and Supabase,
      // so a direct browser-to-DNS-provider call would always be blocked.
      if (!domainCheckError && domainCheck && domainCheck.ok === false) {
        setError(t("entry.err.invalidEmailDomain"));
        setStep("self-register");
        return;
      }

      let { data: signUpData, error: signUpError } = await signUpWithPassword(emailNorm, password);

      if (signUpError) {
        const msg = (signUpError.message || "").toLowerCase();

        if (msg.includes("already")) {
          // The auth account exists, but that alone doesn't mean
          // registration ever finished -- a previous attempt may have
          // created the auth user and then failed before the employee row
          // was created (network drop, validation error, etc.), leaving a
          // dead end: signup says "already registered", but login would say
          // "no account found". If these are the same credentials, signing
          // in recovers a session so we can just finish registration now
          // instead of sending them into that loop.
          const { data: signInData, error: signInError } = await signInWithPassword(emailNorm, password);

          if (signInError || !signInData.user?.id) {
            window.alert(t("entry.alert.userExists"));
            setStep("login");
            return;
          }

          signUpData = signInData;
          signUpError = null;
        } else {
          setError(signUpError.message);
          setStep("self-register");
          return;
        }
      }

      if (!signUpData?.user?.id) {
        setError(t("entry.err.selfRegisterFailed"));
        setStep("self-register");
        return;
      }

      if (pendingCompanyAlias) {
        // Best-effort: a failed alias write shouldn't block registration.
        await addCompanyAlias(companyId, pendingCompanyAlias);
      }

      const res = await registerSelf(signUpData.user.id, companyId);

      if (!res.ok) {
        if (res.reason === "employee_exists") {
          // The recovered sign-in turned out to belong to a fully
          // registered account after all -- just log them in instead of
          // showing a raw backend reason code as an error.
          await linkAfterAuth(signUpData.session?.access_token);
          return;
        }

        setError(
          res.reason === "invalid_email_domain"
            ? t("entry.err.invalidEmailDomain")
            : res.reason ?? t("entry.err.selfRegisterFailed")
        );
        setStep("self-register");
        return;
      }

      await redirectAfterAuth(res.employee_id);
    } catch (e: any) {
      setError(e?.message ?? t("entry.err.selfRegisterFailed"));
      setStep("self-register");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 6 },
        bgcolor: "#05070B",
        backgroundImage:
          "linear-gradient(90deg, rgba(5,7,11,0.96) 0%, rgba(5,7,11,0.84) 42%, rgba(127,29,29,0.20) 100%), url('/background.png')",
        backgroundSize: "cover",
        backgroundPosition: { xs: "62% center", md: "center" },
      }}
    >
      <Paper
        sx={{
          p: { xs: 2.25, sm: 3 },
          width: "100%",
          maxWidth: 540,
          boxSizing: "border-box",
          borderRadius: 2,
          border: "1px solid rgba(255,255,255,0.34)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.36)",
        }}
        elevation={0}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              component="img"
              src="/logo.png"
              alt="takeKONTROL"
              sx={{
                width: 42,
                height: 42,
                objectFit: "contain",
                borderRadius: 2,
                bgcolor: "#F9FAFB",
                p: 0.55,
                border: "1px solid #E5E7EB",
              }}
            />
            <Typography variant="h6" fontWeight={900} sx={{ color: "#111827" }}>
              takeKONTROL
            </Typography>
          </Stack>

          {error && (
            <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {step === "email" && (
            <>
              <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                {t("entry.email.title")}
              </Typography>

              <TextField
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("entry.email.placeholder")}
                fullWidth
                autoFocus
                sx={textFieldSx}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitEmail();
                }}
              />

              <Button variant="contained" size="large" onClick={submitEmail}>
                {t("entry.email.continue")}
              </Button>

              <Button
                variant="text"
                onClick={() => {
                  setError(null);
                  setPassword("");
                  setShowPassword(false);
                  setLegalAccepted(false);
                  setStep("self-register");
                }}
                sx={{ textTransform: "none", fontWeight: 800 }}
              >
                {t("entry.email.register")}
              </Button>
            </>
          )}

          {step === "login" && (
            <>
              <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                {t("entry.password.login.title")}
              </Typography>

              <TextField
                label={t("entry.password.placeholder")}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                autoFocus
                sx={textFieldSx}
                onKeyDown={(e) => {
                  if (e.key === "Enter") login();
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button variant="contained" size="large" onClick={login}>
                {t("entry.password.login.cta")}
              </Button>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ mt: 0.5 }}
              >
                <Button
                  variant="text"
                  onClick={openForgot}
                  sx={{
                    textTransform: "none",
                    fontWeight: 900,
                    color: "#001236",
                    px: 0,
                    minWidth: "auto",
                    "&:hover": {
                      bgcolor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {t("entry.password.forgot")}
                </Button>

                <Button
                  variant="text"
                  onClick={() => {
                    setError(null);
                    setPassword("");
                    setShowPassword(false);
                    setStep("email");
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 900,
                    color: "#001236",
                    px: 0,
                    minWidth: "auto",
                    "&:hover": {
                      bgcolor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {t("entry.password.useDifferent")}
                </Button>
              </Stack>
            </>
          )}

          {step === "set-password" && (
            <>
              <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                {t("entry.password.set.title")}
              </Typography>

              <TextField
                label={t("entry.password.placeholder")}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                autoFocus
                sx={textFieldSx}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setInitialPassword();
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button variant="contained" size="large" onClick={setInitialPassword}>
                {t("entry.password.set.cta")}
              </Button>

              <Button
                variant="text"
                onClick={() => {
                  setError(null);
                  setPassword("");
                  setShowPassword(false);
                  setStep("email");
                }}
                sx={{ textTransform: "none", fontWeight: 800 }}
              >
                {t("entry.back")}
              </Button>
            </>
          )}

          {step === "self-register" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid #FCA5A5",
                  bgcolor: "#FEF2F2",
                }}
              >
                <Typography sx={{ color: "#7F1D1D", fontWeight: 900 }}>
                  {t("entry.register.loginPrompt")}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  aria-label={t("entry.register.loginLink")}
                  onClick={openLoginLookup}
                  sx={{
                    flexShrink: 0,
                    bgcolor: "#991B1B",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#7F1D1D", boxShadow: "none" },
                  }}
                >
                  {t("entry.register.loginCta")}
                </Button>
              </Box>

              <Stack spacing={0.5}>
                <Typography variant="h5" fontWeight={900} sx={{ color: "#111827" }}>
                  {t("entry.register.title")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6B7280" }}>
                  {t("entry.register.subtitle")}
                </Typography>
              </Stack>

              <TextField
                label={t("entry.email.title")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("entry.email.placeholder")}
                fullWidth
                autoFocus
                sx={textFieldSx}
              />

              <TextField
                label={t("entry.password.placeholder")}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("entry.password.placeholder")}
                fullWidth
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack spacing={1}>
                <TextField
                  label={t("entry.register.companyPlaceholder")}
                  placeholder={t("entry.register.companyPlaceholder")}
                  value={companyText}
                  onChange={(e) => onCompanyTextChange(e.target.value)}
                  disabled={companyDecision !== "idle"}
                  fullWidth
                  sx={textFieldSx}
                  InputProps={{
                    endAdornment: companySuggestionsLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />

                {companyDecision === "confirmed-existing" && (
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      {t("entry.register.companyMatchedNote").replace("{name}", companyText)}
                    </Typography>
                    <Link component="button" type="button" variant="body2" onClick={onChangeCompany}>
                      {t("entry.register.companyChange")}
                    </Link>
                  </Stack>
                )}

                {companyDecision === "confirmed-new" && (
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      {t("entry.register.companyNewNote").replace("{name}", companyText)}
                    </Typography>
                    <Link component="button" type="button" variant="body2" onClick={onChangeCompany}>
                      {t("entry.register.companyChange")}
                    </Link>
                  </Stack>
                )}

                {companyDecision === "idle" && companyText.trim() !== "" && (
                  <Stack spacing={0.75}>
                    {companySuggestions.length > 0 && (
                      <Stack spacing={0.5}>
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          {t("entry.register.companySuggestionPrompt")}
                        </Typography>
                        {companySuggestions.map((suggestion) => (
                          <Stack
                            key={suggestion.id}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flexWrap: "wrap" }}
                          >
                            <Typography variant="body2" fontWeight={700} sx={{ color: "#111827" }}>
                              {suggestion.name}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onConfirmCompanySuggestion(suggestion)}
                            >
                              {t("entry.register.companySuggestionConfirm")}
                            </Button>
                            <Button size="small" onClick={() => onRejectCompanySuggestion(suggestion)}>
                              {t("entry.register.companySuggestionReject")}
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={onOpenNewCompanyDialog}
                      sx={{ textAlign: "left" }}
                    >
                      {t("entry.register.companyUseAsNew").replace("{name}", companyText.trim())}
                    </Link>
                  </Stack>
                )}
              </Stack>

              <Dialog
                open={newCompanyDialogOpen}
                onClose={onCloseNewCompanyDialog}
                fullScreen={isMobileViewport}
                fullWidth
                maxWidth="xs"
              >
                <DialogTitle sx={{ fontWeight: 900 }}>{t("entry.register.newCompanyDialogTitle")}</DialogTitle>
                <DialogContent>
                  <Stack spacing={2} sx={{ pt: 0.5 }}>
                    <TextField
                      label={t("entry.register.newCompanyDialogNameLabel")}
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      autoFocus
                      fullWidth
                      sx={textFieldSx}
                    />
                    <TextField
                      label={t("entry.register.newCompanyDialogAliasLabel")}
                      helperText={t("entry.register.newCompanyDialogAliasHelper")}
                      value={newCompanyAlias}
                      onChange={(e) => setNewCompanyAlias(e.target.value)}
                      fullWidth
                      sx={textFieldSx}
                    />
                    {newCompanyError && (
                      <Typography variant="body2" sx={{ color: "#B91C1C" }}>
                        {newCompanyError}
                      </Typography>
                    )}
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                  <Button onClick={onCloseNewCompanyDialog} disabled={newCompanySubmitting}>
                    {t("entry.register.newCompanyDialogCancel")}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={onSubmitNewCompanyDialog}
                    disabled={newCompanySubmitting}
                  >
                    {newCompanySubmitting ? (
                      <CircularProgress size={18} sx={{ color: "inherit" }} />
                    ) : (
                      t("entry.register.newCompanyDialogSave")
                    )}
                  </Button>
                </DialogActions>
              </Dialog>

              <Autocomplete
                options={DEPARTMENT_OPTIONS}
                value={DEPARTMENT_OPTIONS.find((d) => d.value === department) ?? null}
                onChange={(_, value) => {
                  setDepartment(value?.value ?? "");
                }}
                getOptionLabel={(option) => L(option.en, option.de)}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("entry.register.departmentPlaceholder")}
                    placeholder={t("entry.register.departmentPlaceholder")}
                    fullWidth
                    sx={textFieldSx}
                  />
                )}
              />

              <Box
                sx={{
                  width: "100%",
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: "1px solid #E5E7EB",
                  bgcolor: "#F9FAFB",
                  boxSizing: "border-box",
                }}
              >
                <FormControlLabel
                  sx={{ mx: 0, alignItems: "center" }}
                  control={
                    <Switch
                      checked={registerAnonymous}
                      onChange={(e) => setRegisterAnonymous(e.target.checked)}
                    />
                  }
                  label={t("entry.register.anonymousToggle")}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6B7280",
                    lineHeight: 1.45,
                    pl: { xs: 0, sm: 6.5 },
                    mt: 0.5,
                  }}
                >
                  {t("entry.register.anonymousHelper")}
                </Typography>
              </Box>

              {!registerAnonymous && (
                <>
                  <TextField
                    label={t("entry.register.firstName")}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t("entry.register.firstName")}
                    fullWidth
                    sx={textFieldSx}
                  />

                  <TextField
                    label={t("entry.register.lastName")}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t("entry.register.lastName")}
                    fullWidth
                    sx={textFieldSx}
                  />

                  <TextField
                    label={t("entry.register.positionPlaceholder")}
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    placeholder={t("entry.register.positionPlaceholder")}
                    fullWidth
                    sx={textFieldSx}
                  />

                  <TextField
                    label={t("entry.register.personalAddress")}
                    value={personalAddress}
                    onChange={(e) => setPersonalAddress(e.target.value)}
                    placeholder={t("entry.register.personalAddress")}
                    fullWidth
                    sx={textFieldSx}
                  />
                </>
              )}

              <FormControlLabel
                sx={{
                  alignItems: "flex-start",
                  mx: 0,
                  mt: 0.5,
                }}
                control={
                  <Checkbox
                    checked={legalAccepted}
                    onChange={(e) => setLegalAccepted(e.target.checked)}
                    sx={{
                      color: "#9CA3AF",
                      p: 0,
                      mr: 1.25,
                      mt: 0.25,
                      "&.Mui-checked": { color: "#111827" },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.5 }}>
                    {L("I agree to the ", "Ich akzeptiere die ")}
                    <Link
                      href="/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                      sx={{ color: "#111827", fontWeight: 900 }}
                    >
                      {L("Privacy Policy", "Datenschutzerklärung")}
                    </Link>
                    {L(" and ", " und die ")}
                    <Link
                      href="/terms-and-conditions"
                      target="_blank"
                      rel="noreferrer"
                      sx={{ color: "#111827", fontWeight: 900 }}
                    >
                      {L("Terms & Conditions", "AGB")}
                    </Link>
                    .
                  </Typography>
                }
              />

              <Button
                variant="contained"
                size="large"
                onClick={submitSelfRegistration}
                disabled={!legalAccepted}
              >
                {t("entry.register.cta")}
              </Button>

            </>
          )}

          {step === "forgot" && (
            <>
              <Typography sx={{ fontWeight: 900, color: "#111827" }}>
                {t("entry.reset.title")}
              </Typography>

              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {t("entry.reset.subtitle")}
              </Typography>

              <TextField
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="example@company.com"
                fullWidth
                autoFocus
                sx={textFieldSx}
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendResetEmail();
                }}
              />

              <Button variant="contained" onClick={sendResetEmail}>
                {t("entry.reset.cta")}
              </Button>

              {forgotInfo && (
                <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 800 }}>
                  {forgotInfo}
                </Typography>
              )}

              <Button
                variant="text"
                onClick={() => {
                  setForgotInfo(null);
                  setError(null);
                  setStep("login");
                }}
                sx={{ textTransform: "none", fontWeight: 800 }}
              >
                {t("entry.reset.backToLogin")}
              </Button>
            </>
          )}

          {step === "loading" && <Typography>{t("entry.loading")}</Typography>}
        </Stack>
      </Paper>
    </Box>
  );
}
